import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { DatePicker, TimePicker, Button, Input } from "antd";
import ReactDatePicker from "react-datepicker";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { Dropdown, Modal } from "react-bootstrap";
import {
  createShift,
  useGetShifts,
  assignShift,
  updateAssignedShift,
  AssignedShift,
  Shift,
  deleteShift,
} from "../../../core/api/useShift";
import useGetEmployees from "../../../core/api/useGetEmployees";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useGetAssignedShifts } from "../../../core/api/useShift";
import { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import {
  getAttendanceWidgets,
  AttendanceWidgetStats,
} from "../../../core/api/useAttendance";
import { fetchEmployeeImage } from "../../../core/api/fetchEmployeeImage";

const ScheduleTiming = () => {
  const chartHeight = 250;
  const topMargin = 50;
  const gridLines = 5;
  const stepY = chartHeight / (gridLines - 1);
  const empToken = useAuthToken();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  // Add attendance widgets state
  const [attendanceStats, setAttendanceStats] =
    useState<AttendanceWidgetStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  // Tooltip state for charts
  const [reportTooltip, setReportTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    shift: string;
    date: string;
    value: string;
  } | null>(null);

  const [analyticsTooltip, setAnalyticsTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    label: string;
    value: number;
    percentage: number;
  } | null>(null);

  // 1. Add to state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Load assigned shifts
  const { assignedShifts, loading, error, fetchAssignedShifts } =
    useGetAssignedShifts(empToken);
  const [refreshKey, setRefreshKey] = useState(0);

  // For creating a shift
  const [shiftName, setShiftName] = useState("");
  const [createStartTime, setCreateStartTime] = useState<any>(null);
  const [createEndTime, setCreateEndTime] = useState<any>(null);
  const [isPredefined, setIsPredefined] = useState<boolean>(false);

  // For filtering by department
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedShift, setSelectedShift] = useState("All");
  const [showShiftFilter, setShowShiftFilter] = useState(false);
  // API hooks
  const { employees, isLoading: loadingEmployees } = useGetEmployees();
  const [empImages, setEmpImages] = useState<Record<string, string>>({});
  const {
    shifts,
    loading: loadingShifts,
    fetchShifts,
  } = useGetShifts(empToken);
  const [localShifts, setLocalShifts] = useState<Shift[]>([]);

  // For assigning a shift
  const [employeeId, setEmployeeId] = useState("");
  const [shiftTypeId, setShiftTypeId] = useState("");
  const [assignStartDate, setAssignStartDate] = useState("");
  const [assignEndDate, setAssignEndDate] = useState("");
  const [assignStartTime, setAssignStartTime] = useState<Dayjs | null>(null);
  const [assignEndTime, setAssignEndTime] = useState<Dayjs | null>(null);
  const [assignMaxStartTime, setAssignMaxStartTime] = useState<Dayjs | null>(
    null
  );
  const [assignMaxEndTime, setAssignMaxEndTime] = useState<Dayjs | null>(null);
  const [assignBreakTime, setAssignBreakTime] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedShiftPredefined, setSelectedShiftPredefined] = useState(false);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Fetch attendance stats
  const fetchAttendanceStats = async (dayjsDate?: Dayjs | null) => {
    if (!empToken) return;

    setLoadingStats(true);
    try {
      const targetDate = dayjsDate ?? selectedDate ?? dayjs();

      const stats = await getAttendanceWidgets(
        targetDate.month() + 1, // ✅ month (1-based)
        targetDate.year(), // ✅ year
        targetDate, // optional, for frontend display maybe
        targetDate.date(), // ✅ pass actual day to backend!
        null,
        empToken
      );
      setAttendanceStats(stats);
    } catch (error) {
      console.error("Failed to fetch attendance stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Load attendance stats on component mount and when month/year changes
  useEffect(() => {
    fetchAttendanceStats(selectedDate);
  }, [selectedDate, empToken]);

  useEffect(() => {
    const loadImages = async () => {
      const uniqueEmployeeIds = new Set(
        employees.map((emp) => emp.employee_id).filter(Boolean)
      );
      for (const empId of Array.from(uniqueEmployeeIds)) {
        if (!empImages[empId] && empToken) {
          const imgUrl = await fetchEmployeeImage(empId, empToken);
          setEmpImages((prev) => ({ ...prev, [empId]: imgUrl }));
        }
      }
    };
    if (employees.length > 0 && empToken) {
      loadImages();
    }
  }, [employees, empToken]);

  const handleDateChange = (day: Date | null) => {
    const newDate = dayjs(day);
    setSelectedDate(newDate);
    // Immediately fetch data for the new date
    fetchAttendanceStats(newDate);
    // Also refresh assigned shifts data
    fetchAssignedShifts();
  };

  React.useEffect(() => {
    if (shifts) setLocalShifts(shifts);
  }, [shifts]);

  // Extracted refreshShifts function for reusability
  const refreshShifts = useCallback(async () => {
    try {
      const data = await fetchShifts();
      setLocalShifts(data || []);
    } catch (err) {
      console.error("Failed to refresh shifts:", err);
    }
  }, [fetchShifts]);

  // Initial load only
  useEffect(() => {
    const loadShifts = async () => {
      try {
        const data = await fetchShifts();
        setLocalShifts(data || []);
      } catch (err) {
        console.error("Failed to load shifts:", err);
      }
    };

    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ empty deps, runs once

  // Helper functions for widgets
  const getShiftIcon = (shiftName: string) => {
    const name = shiftName.toLowerCase();
    if (name.includes("morning")) return "ti ti-sunrise";
    if (name.includes("night")) return "ti ti-moon-stars";
    if (name.includes("evening")) return "ti ti-sun";
    if (name.includes("general")) return "ti ti-calendar-time";
    return "ti ti-clock";
  };

  const getShiftColor = (shiftName: string) => {
    const name = shiftName.toLowerCase();
    if (name.includes("morning")) return "warning";
    if (name.includes("night")) return "dark";
    if (name.includes("evening")) return "primary";
    if (name.includes("general")) return "success";
    return "info";
  };

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};
    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });
    return map;
  }, [employees]);

  // Create shift ID to name map
  const shiftMap = useMemo(() => {
    const map: Record<number, string> = {};
    shifts.forEach((s) => {
      map[s.shift_type_id] = s.shift_name;
    });
    return map;
  }, [shifts]);

  // Dynamic widget data calculation from API
  const widgetData = useMemo(() => {
    if (!attendanceStats) return [];

    return Object.entries(attendanceStats.shift_assign_count || {}).map(
      ([shiftName, totalEmployees]) => {
        const presentEmployees =
          attendanceStats.shift_present?.[shiftName] || 0;
        const absentEmployees = attendanceStats.shift_absent?.[shiftName] || 0;

        return {
          id: shiftName,
          title: shiftName,
          totalEmployees: totalEmployees,
          presentEmployees: presentEmployees,
          absentEmployees: absentEmployees,
          icon: getShiftIcon(shiftName),
          color: getShiftColor(shiftName),
        };
      }
    );
  }, [attendanceStats]);

  const selectedMonthData = attendanceStats;

  const dynamicShiftNames = useMemo(() => {
    return Object.keys(attendanceStats?.shift_assign_count || {});
  }, [attendanceStats]);

  const scrollWidgets = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    scrollRef.current.scrollLeft +=
      direction === "right" ? scrollAmount : -scrollAmount;
  };

  const updateArrowVisibility = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateArrowVisibility();
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", updateArrowVisibility);
      }
      window.removeEventListener("resize", updateArrowVisibility);
    };
  }, []);

  // Enhanced assigned data with date filtering
  const assignedData = useMemo(() => {
    return assignedShifts.map((item) => {
      const emp = employeeNameMap[item.employee_id];

      let formattedBreakTime = "0 hr";
      if (item.break_time) {
        const [hours, minutes] = item.break_time.split(":");
        const hr = parseInt(hours, 10);
        const min = parseInt(minutes, 10);
        formattedBreakTime = `${hr} hr${min > 0 ? ` ${min} min` : ""}`;
      }

      const isPredefined = item.shift_type?.is_predefined;

      return {
        ...item,
        key: item.employee_shift_id,
        Name: emp?.name || "N/A",
        Department: {
          department: emp?.department || "N/A",
          role: emp?.role || "N/A",
        },
        ShiftName: item.shift_type?.shift_name || "—",
        Dates: `${item.start_date} → ${item.end_date}`,
        Times: isPredefined
          ? `${item.shift_type?.shift_start_time} → ${item.shift_type?.shift_end_time}`
          : `${item.start_time} → ${item.end_time}`,
        break_time: formattedBreakTime,
      };
    });
  }, [assignedShifts, employeeNameMap]);

  const filteredAssignedData = useMemo(() => {
    return assignedData.filter((item) => {
      const matchesDepartment =
        selectedDepartment === "All" ||
        item.Department.department === selectedDepartment;

      const matchesShift =
        selectedShift === "All" ||
        item.shift_type?.shift_name === selectedShift;

      const search = searchTerm.toLowerCase();

      const matchesSearch =
        search === "" ||
        item.Name.toLowerCase().includes(search) ||
        item.Department.department.toLowerCase().includes(search) ||
        item.ShiftName.toLowerCase().includes(search) ||
        item.Dates.toLowerCase().includes(search) ||
        item.Times.toLowerCase().includes(search);

      return matchesDepartment && matchesShift && matchesSearch;
    });
  }, [assignedData, selectedDepartment, selectedShift, searchTerm]);

  const resetForm = () => {
    setSelectedDepartment("");
    setEmployeeId("");
    setShiftTypeId("");
    setAssignStartDate("");
    setAssignEndDate("");
    setAssignStartTime(null);
    setAssignEndTime(null);
    setAssignMaxStartTime(null);
    setAssignMaxEndTime(null);
    setAssignBreakTime("");
    setIsActive(true);
    setSelectedShiftPredefined(false);
    setIsEditMode(false);
    setEditingRecordId(null);
  };

  const handleShiftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setShiftTypeId(selectedId);

    const selectedShift = shifts.find(
      (s) => String(s.shift_type_id) === selectedId
    );

    // ✅ Use `is_predefined` instead of `predefined`
    setSelectedShiftPredefined(!!selectedShift?.is_predefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employeeId || !shiftTypeId) {
      toast.warning("Please select both employee and shift");
      return;
    }

    const isPredefinedShift = selectedShiftPredefined;

    if (!isPredefinedShift) {
      if (!assignStartTime || !assignEndTime) {
        toast.warning("Please fill all required fields.");
        return;
      }
    }

    const payload = {
      employee_id: employeeId,
      shift_type_id: Number(shiftTypeId),
      start_date: assignStartDate,
      end_date: assignEndDate || null,
      start_time: selectedShiftPredefined
        ? null
        : assignStartTime?.format("HH:mm:ss") || null,
      end_time: selectedShiftPredefined
        ? null
        : assignEndTime?.format("HH:mm:ss") || null,
      max_start_time: selectedShiftPredefined
        ? null
        : assignMaxStartTime?.format("HH:mm:ss") || null,
      max_end_time: selectedShiftPredefined
        ? null
        : assignMaxEndTime?.format("HH:mm:ss") || null,
      break_time: assignBreakTime
        ? dayjs()
            .startOf("day")
            .add(Number(assignBreakTime), "hour")
            .format("HH:mm:ss")
        : "00:00:00",
      is_active: isActive,
    };
    try {
      if (isEditMode && editingRecordId) {
        await updateAssignedShift(editingRecordId, payload, empToken);
      } else {
        await assignShift(payload, empToken);
      }

      resetForm();

      // ✅ Refresh immediately
      await fetchAssignedShifts();
      await fetchAttendanceStats();
      await refreshShifts();
    } catch (err) {
      console.error("Shift assign/edit error:", err);
    }
  };

  const resetShiftForm = () => {
    setShiftName("");
    setCreateStartTime(null);
    setCreateEndTime(null);
    setIsPredefined(false);
  };

  const handleSaveShift = async () => {
    if (!shiftName || (!isPredefined && (!createStartTime || !createEndTime))) {
      toast.warning("All required fields must be filled");
      return;
    }
    try {
      await createShift(
        {
          shift_name: shiftName,
          shift_start_time: createStartTime.format("HH:mm:ss"),
          shift_end_time: createEndTime.format("HH:mm:ss"),
          is_predefined: isPredefined,
        },
        empToken // ✅ Pass token here
      );

      // ✅ Reset form after save
      resetShiftForm();
      await refreshShifts(); // NEW: Manually refresh localShifts after create
    } catch (err) {
      console.error("Error saving shift:", err);
    }
  };

  const handleEditClick = (record: AssignedShift) => {
    setIsEditMode(true);
    setEditingRecordId(record.employee_shift_id);

    const shift = shifts.find((s) => s.shift_type_id === record.shift_type_id);
    const isPredefined = shift?.is_predefined ?? false;
    setSelectedShiftPredefined(isPredefined);

    setEmployeeId(record.employee_id);
    setShiftTypeId(String(record.shift_type_id));

    // ✅ Always prefer record's assigned values
    setAssignStartDate(record.start_date ?? "");
    setAssignEndDate(record.end_date ?? "");
    setAssignStartTime(
      record.start_time ? dayjs(record.start_time, "HH:mm:ss") : null
    );
    setAssignEndTime(
      record.end_time ? dayjs(record.end_time, "HH:mm:ss") : null
    );
    setAssignMaxStartTime(
      record.max_start_time ? dayjs(record.max_start_time, "HH:mm:ss") : null
    );
    setAssignMaxEndTime(
      record.max_end_time ? dayjs(record.max_end_time, "HH:mm:ss") : null
    );

    // ✅ Optional: If record doesn’t have dates AND it's predefined, then clear
    if (isPredefined && !record.start_date && !record.end_date) {
      setAssignStartDate("");
      setAssignEndDate("");
      setAssignStartTime(null);
      setAssignEndTime(null);
      setAssignMaxStartTime(null);
      setAssignMaxEndTime(null);
    }

    setAssignBreakTime(record.break_time || "");
    setIsActive(record.is_active);
  };

  const handleDeleteClick = async (record: AssignedShift) => {
    // Validate input
    if (!record?.employee_shift_id) {
      toast.error("Invalid shift record. Please try again.");
      return;
    }

    if (!empToken) {
      toast.error("Authentication token is missing. Please log in again.");
      return;
    }

    try {
      await deleteShift(record.employee_shift_id, empToken);

      // ✅ Refresh immediately
      await fetchAssignedShifts();
      await fetchAttendanceStats();
      await refreshShifts();

      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      console.error("Failed to delete shift:", error);
    }
  };

  const handleToggleActive = async (id: number, newStatus: boolean) => {
    try {
      const shift = assignedShifts.find((s) => s.employee_shift_id === id);
      if (!shift) throw new Error("Assigned shift not found");

      const payload = {
        employee_id: shift.employee_id,
        shift_type_id: shift.shift_type_id,
        start_date: shift.start_date,
        end_date: shift.end_date,
        start_time: shift.start_time,
        end_time: shift.end_time,
        max_start_time: shift.max_start_time,
        max_end_time: shift.max_end_time,
        break_time: shift.break_time || "00:00:00",
        is_active: newStatus,
      };

      await updateAssignedShift(id, payload, empToken);

      // ✅ Refresh immediately
      await fetchAssignedShifts();
      await fetchAttendanceStats();
      await refreshShifts();

      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to update active status:", error);
    }
  };

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    if (!attendanceStats) return { total: 0, present: 0, absent: 0 };

    const total = attendanceStats.all_employee || 0;
    const present = attendanceStats.present || 0;
    const absent = attendanceStats.Total_absent || 0;

    return { total, present, absent };
  }, [attendanceStats]);

  // Calculate percentages for donut chart
  const calculatePercentages = (
    total: number,
    present: number,
    absent: number
  ) => {
    if (total === 0)
      return { presentPerc: 0, absentPerc: 0, presentAngle: 0, absentAngle: 0 };

    const presentPerc = Math.round((present / total) * 100);
    const absentPerc = Math.round((absent / total) * 100);
    const presentAngle = (present / total) * 502; // 502 is the total circumference for r=80
    const absentAngle = (absent / total) * 502;

    return { presentPerc, absentPerc, presentAngle, absentAngle };
  };

  const { presentPerc, absentPerc, presentAngle, absentAngle } =
    calculatePercentages(
      analyticsData.total,
      analyticsData.present,
      analyticsData.absent
    );

  const columns: ColumnsType<AssignedShift> = [
    {
      title: "S.NO",
      key: "serial",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
      render: (value: string, record: AssignedShift) => {
        const imageUrl =
          empImages[record.employee_id] ||
        `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`;
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              key={imageUrl}
              src={imageUrl}
              alt="/assets/img/defaultavatar.png"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                objectFit: "cover", // Ensure the image fits well
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `${process.env.PUBLIC_URL}/assets/img/defaultavatar.png`; // Fallback if default fails
              }}
            />
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "Department",
      render: (value: { department: string; role: string }) => (
        <div>
          <h6>{value.department}</h6>
          <span className="text-muted small">{value.role}</span>
        </div>
      ),
    },
    {
      title: "Shift Name",
      dataIndex: "ShiftName",
      render: (value: string) => {
        let iconClass = "ti ti-clock"; // default icon
        let badgeColor = "bg-soft-info";

        const shiftNameLower = value.toLowerCase();

        if (shiftNameLower.includes("morning")) {
          iconClass = "ti ti-sunrise";
          badgeColor = "bg-soft-warning";
        } else if (shiftNameLower.includes("night")) {
          iconClass = "ti ti-moon-stars";
          badgeColor = "bg-soft-secondary";
        } else if (shiftNameLower.includes("general")) {
          iconClass = "ti ti-calendar-time";
          badgeColor = "bg-soft-success";
        } else if (shiftNameLower.includes("evening")) {
          iconClass = "ti ti-sun";
          badgeColor = "bg-soft-primary";
        }

        return (
          <span
            className={`badge rounded-pill ${badgeColor} text-dark d-inline-flex align-items-center gap-1`}
          >
            <i className={iconClass} style={{ fontSize: "14px" }} />
            {value}
          </span>
        );
      },
    },
    {
      title: "Start/End Date",
      dataIndex: "Dates",
      render: (value: string) => {
        const [startDate, endDate] =
          value?.split("→").map((s) => s.trim()) ?? [];

        const displayOrDash = (dateStr: string | undefined) =>
          dateStr && dateStr.toLowerCase() !== "null" ? dateStr : "—";

        return (
          <div className="d-flex flex-column gap-1">
            <span className="text-dark">{displayOrDash(startDate)}</span>
            <span className="text-dark">{displayOrDash(endDate)}</span>
          </div>
        );
      },
    },
    {
      title: "Start/End Time",
      dataIndex: "Times",
      render: (value: string) => {
        const [startTime, endTime] = value.split("→").map((s) => s.trim());

        const formatTo12Hour = (timeStr: string) =>
          dayjs(timeStr, "HH:mm:ss").format("hh:mm A");

        return (
          <div className="d-flex flex-column gap-1">
            <div>
              <span className="badge bg-success-subtle text-success me-2">
                {formatTo12Hour(startTime)}
              </span>
            </div>
            <div>
              <span className="badge bg-danger-subtle text-danger me-2">
                {formatTo12Hour(endTime)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Break Time",
      dataIndex: "break_time",
      render: (value: string) => (
        <span>
          <span
            className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: "12px" }} />
          </span>
          {value || "—"}
        </span>
      ),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      render: (value: boolean, record: AssignedShift) => (
        <div className="form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input"
            checked={value}
            onChange={(e) =>
              handleToggleActive(record.employee_shift_id, e.target.checked)
            }
          />
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: any, record: AssignedShift) => (
        <div className="d-flex align-items-center gap-2">
          {/* Edit Button */}
          <button
            className="btn btn-link p-0 d-inline-flex align-items-center"
            onClick={() => handleEditClick(record)}
            data-bs-toggle="modal"
            data-bs-target="#assignShiftModal"
          >
            <EditOutlined style={{ fontSize: "16px" }} />
          </button>

          {/* Delete Button */}
          <button
            className="btn btn-link p-0 d-inline-flex align-items-center text-danger"
            onClick={() => handleDeleteClick(record)}
          >
            <DeleteOutlined style={{ fontSize: "16px" }} />
          </button>
        </div>
      ),
    },
  ];

  const departments = useMemo(() => {
    const deptSet = new Set<string>();
    deptSet.add("All"); // Default option

    employees.forEach((emp) => {
      if (emp.department_name) {
        deptSet.add(emp.department_name);
      }
    });

    return Array.from(deptSet);
  }, [employees]);

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Schedule Timing</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Schedule Timing
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              {/* Add Shift Button */}
              <div
                className="d-flex align-items-center"
                style={{ height: "38px" }}
              >
                <ReactDatePicker
                  selected={selectedDate ? selectedDate.toDate() : null}
                  onChange={handleDateChange}
                  customInput={
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      style={{ height: "38px", minWidth: "130px" }}
                    >
                      <i className="ti ti-calendar me-2" />
                      {selectedDate
                        ? selectedDate.format("DD-MM-YYYY")
                        : "Select Date"}
                    </button>
                  }
                  dateFormat="dd-MM-yyyy"
                  popperPlacement="bottom-start"
                />
              </div>

              <button
                className="btn btn-primary d-inline-flex align-items-center"
                data-bs-toggle="modal"
                data-bs-target="#addShiftModal"
              >
                Add Shift
              </button>

              <button
                className="btn btn-primary d-inline-flex align-items-center"
                data-bs-toggle="modal"
                data-bs-target="#assignShiftModal"
                onClick={() => {
                  resetForm();
                  setIsEditMode(false);
                }}
              >
                Assign Shift
              </button>

              {/* Collapse Header */}
              <CollapseHeader />
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Widgets Section */}
          <div className="card mb-4 position-relative">
            <div className="card-body p-3">
              {/* Filter Info Bar */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <small className="text-muted">
                    <i className="ti ti-calendar me-1"></i>
                    Filtered by:{" "}
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : "All Dates"}
                  </small>
                </div>
                <small className="text-muted">
                  Total Shifts: {widgetData.length}
                </small>
              </div>
              {/* Arrows inside card body */}
              {widgetData.length > 0 && (
                <>
                  <button
                    className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y shadow"
                    style={{ zIndex: 1 }}
                    onClick={() => scrollWidgets("left")}
                    disabled={!canScrollLeft}
                  >
                    <i className="ti ti-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y shadow"
                    style={{ zIndex: 1 }}
                    onClick={() => scrollWidgets("right")}
                    disabled={!canScrollRight}
                  >
                    <i className="ti ti-chevron-right"></i>
                  </button>
                </>
              )}

              {/* Scrollable widget list */}
              <div
                className="d-flex overflow-auto"
                ref={scrollRef}
                style={{
                  gap: "1rem",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: "0.5rem",
                }}
              >
                {widgetData.map((widget) => (
                  <div
                    key={widget.id}
                    className="card flex-shrink-0"
                    style={{ minWidth: "260px", maxWidth: "260px" }}
                  >
                    <div className="card-body">
                      <h6 className="mb-2">{widget.title}</h6>
                      <div className="d-flex align-items-center mb-1">
                        <span
                          className={`bg-${widget.color} text-white rounded me-2`}
                          style={{
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                          }}
                        >
                          <i className={widget.icon}></i>
                        </span>
                        <span className="text-muted small">
                          Total Employees
                        </span>
                      </div>
                      <h3 className="mb-0">{widget.totalEmployees}</h3>
                      <div className="d-flex gap-3 mt-2">
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-success rounded-circle me-1"
                            style={{ width: "8px", height: "8px" }}
                          ></div>
                          <small className="text-muted">
                            Present: {widget.presentEmployees}
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-danger rounded-circle me-1"
                            style={{ width: "8px", height: "8px" }}
                          ></div>
                          <small className="text-muted">
                            Absent: {widget.absentEmployees}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Reports and Analytics Section */}
          <div className="row mb-4">
            {/* Reports Section */}
            <div className="col-xl-8 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Reports</h5>
                </div>

                <div className="card-body">
                  <div
                    className="chart-container position-relative"
                    style={{ height: "300px" }}
                  >
                    <svg width="100%" height="300" viewBox="0 0 800 300">
                      {/* Grid lines */}
                      <g stroke="#f0f0f0">
                        {[...Array(gridLines)].map((_, i) => {
                          const y = topMargin + i * stepY;
                          return (
                            <line
                              key={i}
                              x1="60"
                              y1={y}
                              x2="760"
                              y2={y}
                              strokeDasharray="4"
                            />
                          );
                        })}
                      </g>

                      {/* Y-axis line */}
                      <line
                        x1="60"
                        y1={topMargin}
                        x2="60"
                        y2={topMargin + chartHeight}
                        stroke="#ccc"
                      />

                      {/* X-axis line */}
                      <line
                        x1="60"
                        y1={topMargin + chartHeight}
                        x2="760"
                        y2={topMargin + chartHeight}
                        stroke="#ccc"
                      />

                      {/* Y-axis labels */}
                      {[...Array(gridLines)].map((_, i) => {
                        const y = topMargin + i * stepY + 5; // +5 to center text vertically
                        return (
                          <text
                            key={i}
                            x="55"
                            y={y}
                            fontSize="10"
                            fill="#888"
                            textAnchor="end"
                          >
                            {100 - i * 25}%
                          </text>
                        );
                      })}

                      {/* Bars per shift */}
                      {selectedMonthData &&
                        dynamicShiftNames.map((shift, idx) => {
                          const assign =
                            selectedMonthData.shift_assign_count?.[shift] ?? 0;
                          const present =
                            selectedMonthData.shift_present?.[shift] ?? 0;
                          const total = assign || 1;
                          const percent = (present / total) * 100;
                          const height = (percent / 100) * chartHeight;
                          const y = topMargin + (chartHeight - height);
                          const x = 80 + idx * 140; // wider spacing
                          const color = `url(#gradient${(idx % 5) + 1})`;

                          return (
                            <g key={shift}>
                              <rect
                                x={x}
                                y={y}
                                width="30"
                                height={height}
                                fill={color}
                                rx="4"
                                onMouseMove={(e) =>
                                  setReportTooltip({
                                    visible: true,
                                    x: e.clientX,
                                    y: e.clientY,
                                    shift,
                                    date:
                                      selectedDate?.format("DD/MM/YYYY") ?? "",
                                    value: `${present}/${assign}`,
                                  })
                                }
                                onMouseLeave={() => setReportTooltip(null)}
                              />
                              <text
                                x={x + 15}
                                y={topMargin + chartHeight + 15}
                                fontSize="11"
                                fill="#333"
                                textAnchor="middle"
                              >
                                {shift}
                              </text>
                            </g>
                          );
                        })}

                      {/* Gradient defs */}
                      <defs>
                        {[...Array(5)].map((_, i) => (
                          <linearGradient
                            key={i}
                            id={`gradient${i + 1}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={
                                [
                                  "#3b82f6",
                                  "#06b6d4",
                                  "#10b981",
                                  "#f59e0b",
                                  "#ef4444",
                                ][i]
                              }
                            />
                            <stop
                              offset="100%"
                              stopColor={
                                [
                                  "#8b5cf6",
                                  "#3b82f6",
                                  "#06b6d4",
                                  "#10b981",
                                  "#f59e0b",
                                ][i]
                              }
                            />
                          </linearGradient>
                        ))}
                      </defs>
                    </svg>

                    {/* Tooltip */}
                    {reportTooltip?.visible && (
                      <div
                        className="position-fixed bg-dark text-white p-2 rounded shadow"
                        style={{
                          position: "fixed",
                          top: reportTooltip.y + 10,
                          left: reportTooltip.x + 10,
                          background: "#fff",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          zIndex: 9999,
                          pointerEvents: "none",
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <strong>{reportTooltip.shift}</strong>
                        <div>Date: {reportTooltip.date}</div>
                        <div>Present: {reportTooltip.value}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Section */}
            <div className="col-xl-4 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Analytics</h5>
                </div>

                <div className="card-body d-flex flex-column align-items-center justify-content-center">
                  <>
                    {/* Donut Chart */}
                    <div className="position-relative mb-3">
                      <svg width="200" height="200" viewBox="0 0 200 200">
                        {/* Base circle */}
                        <circle
                          cx="100"
                          cy="100"
                          r="80"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="20"
                        />

                        {/* Present (Green) */}
                        <circle
                          cx="100"
                          cy="100"
                          r="80"
                          fill="none"
                          stroke="#22c55e"
                          strokeWidth="20"
                          strokeDasharray={`${presentAngle} 502`}
                          strokeDashoffset="0"
                          transform="rotate(-90 100 100)"
                          style={{ cursor: "pointer" }}
                          onMouseMove={(e) => {
                            setAnalyticsTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              label: "Present",
                              value: analyticsData.present,
                              percentage: presentPerc,
                            });
                          }}
                          onMouseLeave={() => setAnalyticsTooltip(null)}
                        />

                        {/* Absent (Red) */}
                        <circle
                          cx="100"
                          cy="100"
                          r="80"
                          fill="none"
                          stroke="#ef4444"
                          strokeWidth="20"
                          strokeDasharray={`${absentAngle} 502`}
                          strokeDashoffset={-presentAngle}
                          transform="rotate(-90 100 100)"
                          style={{ cursor: "pointer" }}
                          onMouseMove={(e) => {
                            setAnalyticsTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              label: "Absent",
                              value: analyticsData.absent,
                              percentage: absentPerc,
                            });
                          }}
                          onMouseLeave={() => setAnalyticsTooltip(null)}
                        />

                        {/* Center Text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          fontSize="14"
                          fill="#6b7280"
                        >
                          Total Users
                        </text>
                        <text
                          x="100"
                          y="115"
                          textAnchor="middle"
                          fontSize="28"
                          fontWeight="bold"
                          fill="#111827"
                        >
                          {analyticsData.total}
                        </text>
                      </svg>
                    </div>

                    {/* Legend with actual data */}
                    <div className="d-flex justify-content-between w-100">
                      <div className="d-flex flex-column align-items-center">
                        <div className="d-flex align-items-center mb-1">
                          <div
                            className="bg-primary rounded me-2"
                            style={{ width: "12px", height: "12px" }}
                          ></div>
                          <small className="text-muted">Total</small>
                        </div>
                        <strong>{analyticsData.total}</strong>
                      </div>
                      <div className="d-flex flex-column align-items-center">
                        <div className="d-flex align-items-center mb-1">
                          <div
                            className="bg-success rounded me-2"
                            style={{ width: "12px", height: "12px" }}
                          ></div>
                          <small className="text-muted">Present</small>
                        </div>
                        <strong>{analyticsData.present}</strong>
                      </div>
                      <div className="d-flex flex-column align-items-center">
                        <div className="d-flex align-items-center mb-1">
                          <div
                            className="bg-danger rounded me-2"
                            style={{ width: "12px", height: "12px" }}
                          ></div>
                          <small className="text-muted">Absent</small>
                        </div>
                        <strong>{analyticsData.absent}</strong>
                      </div>
                    </div>
                  </>
                  {/* Analytics Tooltip */}
                  {analyticsTooltip && (
                    <div
                      className="position-fixed bg-dark text-white p-2 rounded shadow"
                      style={{
                        left: analyticsTooltip.x + 10,
                        top: analyticsTooltip.y - 10,
                        zIndex: 1000,
                        fontSize: "12px",
                        pointerEvents: "none",
                      }}
                    >
                      <div>
                        <strong>{analyticsTooltip.label}</strong>
                      </div>
                      <div>{analyticsTooltip.value} employees</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Schedule Timing List</h5>
              <div className="d-flex my-xl-auto justify-content-end align-items-center flex-wrap gap-2 mb-3">
                {/* Shift Name Dropdown Filter */}
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="light"
                    className="border d-flex align-items-center"
                  >
                    <i className="ti ti-filter me-2" />
                    {selectedShift !== "All" ? selectedShift : "Filter Shift"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setSelectedShift("All")}>
                      All Shifts
                    </Dropdown.Item>
                    {shifts.map((shift) => (
                      <Dropdown.Item
                        key={shift.shift_name}
                        onClick={() => setSelectedShift(shift.shift_name)}
                      >
                        {shift.shift_name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Department Dropdown */}
                <Dropdown>
                  <Dropdown.Toggle variant="light" className="border">
                    Department: {selectedDepartment}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {[...departments].map((dept) => (
                      <Dropdown.Item
                        key={dept}
                        onClick={() => setSelectedDepartment(dept)}
                      >
                        {dept}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Search Input (right-most) */}
                <Input.Search
                  placeholder="Search..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
              </div>
            </div>
            <div className="card-body p-0">
              <div className="custom-datatable-filter table-responsive">
                <Table
                  key={refreshKey}
                  dataSource={filteredAssignedData}
                  columns={columns}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Assign Shift Modal */}
      <div className="modal fade" id="assignShiftModal" tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditMode ? "Edit Assign" : "Assign Shift"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {/* Department Dropdown */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Department <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                      <option value="All">All Departments</option>
                      {Array.from(
                        new Set(
                          employees.map(
                            (emp) => emp.department_name || "Unknown"
                          )
                        )
                      )
                        .sort()
                        .map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                    </select>
                  </div>
                  {/* Employee Dropdown */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Employee <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees
                        .filter(
                          (emp) =>
                            (emp.role_name === "Manager" ||
                              emp.role_name === "Employee") &&
                            (selectedDepartment === "All" ||
                              emp.department_name === selectedDepartment)
                        )
                        .map((emp) => (
                          <option key={emp.employee_id} value={emp.employee_id}>
                            {`${emp.firstname} ${emp.lastname} (${emp.role_name})`}
                          </option>
                        ))}
                    </select>
                  </div>
                  {/* Shift Dropdown */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Shift <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={shiftTypeId}
                      onChange={handleShiftChange}
                      required
                    >
                      <option value="">Select Shift</option>
                      {shifts.map((shift) => (
                        <option
                          key={shift.shift_type_id}
                          value={shift.shift_type_id}
                        >
                          {shift.shift_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={assignStartDate}
                      onChange={(e) => setAssignStartDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">End Date (optional)</label>
                    <input
                      type="date"
                      className="form-control"
                      value={assignEndDate}
                      onChange={(e) => setAssignEndDate(e.target.value)}
                    />
                  </div>
                  {/* Conditional fields if NOT predefined shift */}
                  {!selectedShiftPredefined && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">
                          Start Time <span className="text-danger">*</span>
                        </label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          value={assignStartTime}
                          onChange={setAssignStartTime}
                          className="w-100"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          End Time <span className="text-danger">*</span>
                        </label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          value={assignEndTime}
                          onChange={setAssignEndTime}
                          className="w-100"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Max Start Time</label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          value={assignMaxStartTime}
                          onChange={setAssignMaxStartTime}
                          className="w-100"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Max End Time</label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          value={assignMaxEndTime}
                          onChange={setAssignMaxEndTime}
                          className="w-100"
                        />
                      </div>
                    </>
                  )}
                  {/* Always visible - Break Time */}
                  <div className="col-md-6">
                    <label className="form-label">Break Time (in hours)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={assignBreakTime}
                      onChange={(e) => setAssignBreakTime(e.target.value)}
                      min={0}
                    />
                  </div>
                  {/* Active toggle only for new assignments */}
                  {!isEditMode && (
                    <div className="col-md-6 d-flex align-items-center">
                      <label className="form-label me-3">Active</label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={resetForm}
                >
                  Reset
                </button>
                <button type="submit" className="btn btn-primary">
                  {isEditMode ? "Update Shift" : "Assign Shift"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Schedule Modal */}
      <div
        className="modal fade"
        id="addShiftModal"
        tabIndex={-1}
        aria-labelledby="addShiftModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg rounded-4">
            <div className="modal-header border-0">
              <h5 className="modal-title fw-semibold" id="addShiftModalLabel">
                Manage Shifts
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="row g-4">
                {/* Shift Form */}
                <div className="col-md-6 border-end pe-4 d-flex flex-column justify-content-between">
                  <div>
                    <h6 className="mb-3 fw-bold">Add New Shift</h6>
                    <form>
                      <div className="mb-3">
                        <label className="form-label">
                          Shift Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter shift name"
                          value={shiftName}
                          onChange={(e) => setShiftName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          Start Time <span className="text-danger">*</span>
                        </label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          className="form-control"
                          value={createStartTime}
                          onChange={setCreateStartTime}
                          getPopupContainer={() =>
                            document.getElementById("addShiftModal")!
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          End Time <span className="text-danger">*</span>
                        </label>
                        <TimePicker
                          use12Hours
                          format="h:mm A"
                          className="form-control"
                          value={createEndTime}
                          onChange={setCreateEndTime}
                          getPopupContainer={() =>
                            document.getElementById("addShiftModal")!
                          }
                        />
                      </div>
                      <div className="form-check form-switch mb-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="predefinedSwitch"
                          checked={isPredefined}
                          onChange={(e) => setIsPredefined(e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="predefinedSwitch"
                        >
                          Predefined Shift
                        </label>
                      </div>
                    </form>
                  </div>
                  {/* Buttons moved to left side */}
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={resetShiftForm}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      // data-bs-dismiss="modal"
                      onClick={handleSaveShift}
                    >
                      Save Shift
                    </button>
                  </div>
                </div>
                {/* Existing Shifts */}
                <div className="col-md-6 ps-4">
                  <h6 className="mb-3 fw-bold">Existing Shifts</h6>
                  {loading ? (
                    <p>Loading shifts...</p>
                  ) : error ? (
                    <p className="text-danger">{error}</p>
                  ) : localShifts.length === 0 ? (
                    <p className="text-muted">No shifts available.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {localShifts.map((shift) => (
                        <li
                          key={shift.shift_type_id}
                          className="list-group-item text-capitalize ps-0"
                        >
                          <span className="text-primary me-2">◉</span>
                          <span className="text-capitalize">
                            {shift.shift_name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ScheduleTiming;
